<template>
    <!-- Right slide-over (same interaction as Shared/PanelComponent: backdrop + slide-in + close on X/outside),
         themed with the cv3 tokens to match the caisse. -->
    <transition name="cv3-drawer">
        <div v-if="open" class="cv3-drawer-root">
            <div class="cv3-drawer-overlay" @click="$emit('close')"></div>
            <aside class="cv3-drawer" role="dialog" aria-modal="true">
                <header class="cv3-drawer-head">
                    <div>
                        <h2 class="!ppv2-text-white">{{ $t('checkout.v3.open_sales_title') }}</h2>
                        <p class="cv3-drawer-sub">
                            {{ $t('checkout.v3.open_sales_sub', { count: visibleCount, max: store.openSalesMax }) }}
                        </p>
                    </div>
                    <button type="button" class="cv3-drawer-close" @click="$emit('close')"><XIcon :size="20" /></button>
                </header>

                <div class="cv3-drawer-body">
                    <p v-if="!visibleCount && !loading" class="cv3-drawer-empty">{{ $t('checkout.v3.no_open_sales') }}</p>
                    <p v-if="loading" class="cv3-drawer-empty">{{ $t('checkout.v3.loading') }}</p>

                    <!-- Selected customer's open sales -->
                    <section v-if="clientList.length" class="cv3-drawer-section">
                        <button type="button" class="cv3-drawer-section-head" @click="showClient = !showClient">
                            <span>{{ $t('checkout.v3.client_sales_label', { name: store.openSalesClientName }) }}</span>
                            <ChevronDownIcon :size="18" :class="{ rot: !showClient }" />
                        </button>
                        <div v-show="showClient">
                            <OpenSaleCard
                                v-for="o in clientList"
                                :key="'c' + o.id"
                                :order="o"
                                :busy="store.busy"
                                @open="open_(o)"
                                @dismiss="dismiss(o)"
                            />
                        </div>
                    </section>

                    <!-- Recent open sales across the establishment -->
                    <section v-if="globalList.length" class="cv3-drawer-section">
                        <button type="button" class="cv3-drawer-section-head" @click="showGlobal = !showGlobal">
                            <span>{{ $t('checkout.v3.global_sales_label') }}</span>
                            <ChevronDownIcon :size="18" :class="{ rot: !showGlobal }" />
                        </button>
                        <div v-show="showGlobal">
                            <OpenSaleCard
                                v-for="o in globalList"
                                :key="'g' + o.id"
                                :order="o"
                                :busy="store.busy"
                                @open="open_(o)"
                                @dismiss="dismiss(o)"
                            />
                        </div>
                    </section>
                </div>
            </aside>
        </div>
    </transition>
</template>

<script>
import { X as XIcon, ChevronDown as ChevronDownIcon } from 'lucide-vue'
import OpenSaleCard from './OpenSaleCard.vue'

export default {
    name: 'OpenSalesDrawer',
    components: { XIcon, ChevronDownIcon, OpenSaleCard },
    props: {
        store: { type: Object, required: true },
        open: { type: Boolean, default: false },
    },
    data() {
        return { loading: false, showClient: true, showGlobal: true, dismissed: [] }
    },
    computed: {
        clientList() {
            return (this.store.openSalesClient || []).filter((o) => !this.dismissed.includes(o.id))
        },
        globalList() {
            return (this.store.openSalesGlobal || []).filter((o) => !this.dismissed.includes(o.id))
        },
        visibleCount() {
            return this.clientList.length + this.globalList.length
        },
    },
    watch: {
        open(v) {
            if (v) this.load()
        },
    },
    methods: {
        async load() {
            this.dismissed = []
            this.loading = true
            try {
                await this.store.fetchOpenSales()
            } finally {
                this.loading = false
            }
        },
        async open_(order) {
            try {
                await this.store.openSale(order)
                this.$toast && this.$toast.success(this.$t('checkout.v3.open_sale_success'))
                this.$emit('opened', order)
            } catch (e) {
                this.$toast && this.$toast.error(this.$t('checkout.v3.error_opening_sale'))
            }
        },
        // Local dismiss (hides the card from the current view; reappears on next fetch).
        dismiss(order) {
            if (!this.dismissed.includes(order.id)) this.dismissed.push(order.id)
        },
    },
}
</script>

<style scoped lang="scss">
.cv3-drawer-root {
    position: fixed;
    inset: 0;
    z-index: 1200;
}
.cv3-drawer-overlay {
    position: absolute;
    inset: 0;
    background: rgba(3, 6, 20, 0.6);
    backdrop-filter: blur(2px);
}
.cv3-drawer {
    position: absolute;
    bottom: 0;
    right: 0;
    height: 94vh;
    width: 440px;
    max-width: 92vw;
    background: linear-gradient(180deg, #14163a 0%, #0d0f23 100%), var(--cv3-bg);
    border-left: 1px solid var(--cv3-border-input);
    box-shadow: -18px 0 50px -20px rgba(0, 0, 0, 0.7);
    display: flex;
    flex-direction: column;
    font-family: 'Poppins', sans-serif;
    color: var(--cv3-text);
}
.cv3-drawer-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 20px 22px 16px;
    border-bottom: 1px solid var(--cv3-border);
    h2 {
        margin: 0;
        font-size: 19px;
        font-weight: 800;
        letter-spacing: -0.2px;
    }
}
.cv3-drawer-sub {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--cv3-muted);
}
.cv3-drawer-close {
    border: 0;
    background: transparent;
    color: var(--cv3-muted);
    display: inline-flex;
}
.cv3-drawer-body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}
.cv3-drawer-empty {
    color: var(--cv3-muted);
    text-align: center;
    padding: 30px 0;
    font-size: 14px;
}
.cv3-drawer-section-head {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 0;
    background: transparent;
    color: var(--cv3-text);
    font-size: 14px;
    font-weight: 700;
    padding: 4px 2px 10px;
    svg {
        transition: transform 0.2s ease;
        color: var(--cv3-muted);
        &.rot {
            transform: rotate(-90deg);
        }
    }
}

/* slide-in (mirrors PanelComponent's panel-slide) */
.cv3-drawer-enter-active,
.cv3-drawer-leave-active {
    transition: opacity 0.25s ease;
    .cv3-drawer {
        transition: transform 0.3s ease-out;
    }
}
.cv3-drawer-enter,
.cv3-drawer-leave-to {
    opacity: 0;
    .cv3-drawer {
        transform: translateX(100%);
    }
}
</style>
